import type { DocumentSection, ShowcaseSlide } from "@roof-hawk/core";
import { useRef, useState } from "react";

/** How far a finger has to travel sideways before it counts as a swipe. */
const SWIPE_PX = 40;

/**
 * A product shown off on the proposal: a carousel of it installed, with what
 * it does beside each photograph, then its warranty on copper.
 *
 * Built from the Pro Flo design handoff Jonathon supplied on 2026-10-01 ("i
 * want this to show on the estimate to the customer so they can see what it
 * looks like"). The words and photographs are data (`productShowcases.ts` in
 * packages/core); this only draws them.
 *
 * It sits in the middle of a long page, so unlike the colour viewer it does
 * not take the arrow keys for the whole document: they move it only while
 * focus is inside it.
 */
export function ProductShowcaseView({
  section,
}: {
  section: Extract<DocumentSection, { id: "showcase" }>;
}) {
  const { showcase } = section;
  const { slides } = showcase;
  const [index, setIndex] = useState(0);
  const go = (to: number) =>
    setIndex(((to % slides.length) + slides.length) % slides.length);
  const touchX = useRef<number | null>(null);

  return (
    <section className="pdoc-showcase" id="pdoc-showcase">
      <div className="wrap">
        <header className="pdoc-sc-head">
          <div>
            {section.eyebrow ? (
              <p className="pdoc-sc-eyebrow">{section.eyebrow}</p>
            ) : null}
            <h2 className="pdoc-sc-title">{section.heading}</h2>
          </div>
          <p className="pdoc-sc-tag">
            {showcase.tagline[0]}
            <br />
            <b>{showcase.tagline[1]}</b>
          </p>
        </header>

        <div
          className="pdoc-sc-carousel"
          role="region"
          aria-roledescription="carousel"
          aria-label={`${section.heading} features`}
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "ArrowLeft") go(index - 1);
            else if (e.key === "ArrowRight") go(index + 1);
            else return;
            e.preventDefault();
          }}
        >
          <div
            className="pdoc-sc-track"
            style={{ transform: `translateX(${-index * 100}%)` }}
            onTouchStart={(e) => {
              touchX.current = e.changedTouches[0]?.clientX ?? null;
            }}
            onTouchEnd={(e) => {
              const start = touchX.current;
              touchX.current = null;
              const end = e.changedTouches[0]?.clientX;
              if (start == null || end == null) return;
              const dx = end - start;
              if (Math.abs(dx) > SWIPE_PX) go(index + (dx < 0 ? 1 : -1));
            }}
          >
            {slides.map((slide, n) => (
              <Slide
                key={slide.headline}
                slide={slide}
                number={n + 1}
                of={slides.length}
                current={n === index}
              />
            ))}
          </div>
          <button
            type="button"
            className="pdoc-sc-nav prev"
            aria-label="Previous slide"
            onClick={() => go(index - 1)}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M15 18l-6-6 6-6" />
            </svg>
          </button>
          <button
            type="button"
            className="pdoc-sc-nav next"
            aria-label="Next slide"
            onClick={() => go(index + 1)}
          >
            <svg viewBox="0 0 24 24" aria-hidden="true">
              <path d="M9 6l6 6-6 6" />
            </svg>
          </button>
        </div>

        <div className="pdoc-sc-dots">
          {slides.map((slide, n) => (
            <button
              key={slide.headline}
              type="button"
              aria-label={`Go to slide ${n + 1}`}
              aria-current={n === index}
              onClick={() => go(n)}
            />
          ))}
        </div>
        <p className="pdoc-sc-foot">{showcase.footnote}</p>
      </div>
    </section>
  );
}

function Slide({
  slide,
  number,
  of,
  current,
}: {
  slide: ShowcaseSlide;
  number: number;
  of: number;
  current: boolean;
}) {
  const label = `${number} of ${of}`;
  if (slide.figure)
    return (
      <article
        className="pdoc-sc-slide warranty"
        aria-label={label}
        aria-hidden={!current}
      >
        <div className="pdoc-sc-copy">
          <p className="k">{slide.kicker}</p>
          <p className="big">{slide.figure}</p>
          <h3>{slide.headline}</h3>
          {slide.body ? <p>{slide.body}</p> : null}
        </div>
      </article>
    );
  return (
    <article
      className="pdoc-sc-slide"
      aria-label={label}
      aria-hidden={!current}
    >
      <div className="pdoc-sc-media">
        {slide.image ? (
          <img
            src={slide.image}
            alt={slide.alt ?? ""}
            style={
              slide.imagePosition
                ? { objectPosition: slide.imagePosition }
                : undefined
            }
            loading={number === 1 ? "eager" : "lazy"}
          />
        ) : null}
        <span className="num">{String(number).padStart(2, "0")}</span>
      </div>
      <div className="pdoc-sc-copy">
        <p className="k">{slide.kicker}</p>
        <h3>{slide.headline}</h3>
        {slide.body ? <p>{slide.body}</p> : null}
        {slide.points ? (
          <ul>
            {slide.points.map((point) => (
              <li key={point}>{point}</li>
            ))}
          </ul>
        ) : null}
      </div>
    </article>
  );
}
